import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { breakpoints } from '../utils/styles';
import { getMenuIcon } from '../utils/menuIcons';
import { getSelectedCinema } from '../reducers';
import { getSecondaryMenu } from '../reducers/menus';
import Carousel from './common/Carousel';
import { MenuItem } from '../types/menu-item';

type Props = {
  items: MenuItem[];
  area?: any;
  cinema?: any;
  user?: any;
};

const Container = styled.section`
  padding: var(--pad-y) 0 0 0;

  @media screen and (min-width: ${breakpoints.desktop}px) {
    padding: 0.5rem 0 var(--pad-y) 0;
  }
`;

const MenuItemWrapper = styled.div`
  max-width: 7rem;
  padding: 0 0.5rem;
  position: relative;
  text-align: center;
  
  @media screen and (min-width: ${breakpoints.desktop}px) {
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 60%;
      background-color: var(--text-primary);
    }
  }

  @media screen and (min-width: ${breakpoints.desktop}px) {
    max-width: none;
  }
`;

const menuLinkStyles = `
  align-items: center;
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;
  padding: 0.75rem;
  text-decoration: none;

  &:hover {
    color: var(--text-primary);
  }

  @media screen and (min-width: ${breakpoints.desktop}px) {
    flex-direction: row;
    padding: 0.75rem 2rem 0.5rem 1.35rem;
    white-space: nowrap;
    
    &:hover {
      color: var(--primary);
      text-decoration: none;
    }
  }
`;

const noIconStyles = (props: { $hasIcon?: boolean }) => !props.$hasIcon && `
  min-height: 100%;
  
  @media screen and (min-width: ${breakpoints.desktop}px) {
    min-height: auto;
  }
`;

const MenuLink = styled(Link)<{ $hasIcon?: boolean }>`
  ${menuLinkStyles}
  ${noIconStyles}
`;

const MenuLinkExternal = styled.a<{ $hasIcon?: boolean }>`
  ${menuLinkStyles}
  ${noIconStyles}
`;

const IconWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    margin-top: -3px;
    position: absolute;
    top: 0;
    left: 0;

    body.theme-market & {
      margin-top: 1px;
    }
  }
  
  .icon-default {
    display: block;
  }
  
  .icon-active {
    display: none;
  }
  
  @media screen and (min-width: ${breakpoints.desktop}px) {
    width: 2rem;
    height: 2rem;
    
    a:hover & .icon-default {
      display: none;
    }
    
    a:hover & .icon-active {
      display: block;
    }
  }
`;

const MenuTitle = styled.span`
  font-family: var(--ff-title);
  font-size: 1.25rem;
  line-height: 1;
  text-transform: uppercase;
  overflow: hidden;
  display: block;

  .theme-market &,
  .theme-platinum & {
    font-size: 0.95rem;
  }

  @media screen and (min-width: ${breakpoints.desktop}px) {
    font-size: 1.75rem;
    line-height: 2rem;
    max-width: none;
    
    .theme-market &,
    .theme-platinum & {
      font-size: 1.1rem;
    }
  }
`;

class SecondaryMenu extends React.Component<Props> {
  isInternalUrl(url: string): boolean {
    if (!url) return false;
    return url.startsWith('/');
  }

  renderMenuItem(item: MenuItem, index: number) {
    const iconUrl = getMenuIcon(item.icons, false, this.props.cinema);
    const isInternal = this.isInternalUrl(item.url);

    const content = (
      <React.Fragment>
        {iconUrl && (
          <IconWrapper>
            {item.icons && Object.keys(item.icons).map(concept => {
              const conceptIcons = item.icons[concept];
              if (!conceptIcons || !conceptIcons.default) {
                return null;
              }
              
              return (
                <React.Fragment key={concept}>
                  <img 
                    src={conceptIcons.default} 
                    className={`icon-default d-${concept}-only`} 
                    alt="" 
                  />
                  {conceptIcons.active && (
                    <img 
                      src={conceptIcons.active} 
                      className={`icon-active d-${concept}-only`} 
                      alt="" 
                    />
                  )}
                </React.Fragment>
              );
            })}
          </IconWrapper>
        )}
        <MenuTitle>{item.title}</MenuTitle>
      </React.Fragment>
    );

    const LinkComponent = isInternal ? MenuLink : MenuLinkExternal;
    const linkProps = isInternal 
      ? { to: item.url, $hasIcon: !!iconUrl }
      : { href: item.url, target: '_blank', rel: 'noopener noreferrer', $hasIcon: !!iconUrl };

    const cinemaId = this.props.cinema && this.props.cinema.id ? this.props.cinema.id : 'default';

    return (
      <MenuItemWrapper key={`${index}-${cinemaId}`}>
        <LinkComponent {...linkProps}>
          {content}
        </LinkComponent>
      </MenuItemWrapper>
    );
  }

  render() {
    const { items, user } = this.props;

    if (!items || items.length === 0) {
      return null;
    }

    return (
      <Container>
        <div className="container no-width">
          <Carousel>
            {items.map((item, index) => this.renderMenuItem(item, index))}
          </Carousel>
        </div>
      </Container>
    );
  }
}

const mapStateToProps = (state: any) => ({
  cinema: getSelectedCinema(state),
  items: getSecondaryMenu(state),
});

export default connect(mapStateToProps)(SecondaryMenu);
